import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Connects to data-controller="settings-select-next"
export default class extends Controller {
  static targets = ["parent", "spinner", "text", "submit", "error"]
  static values = {
    url: String,
  }

  connect() {
    if (!this.hasUrlValue) {
      console.warn("Missing fetch URL!")
    }
    this.lastReloadUrl = null
    this.lastQuery = null
  }

  enableSubmit() {
    const submitButton = this.element.querySelector('input[type="submit"], button[type="submit"], button:not([type])')
    if (submitButton) {
      submitButton.removeAttribute('disabled')
    } else {
      // If not found in controller scope, try the parent form
      const form = this.element.closest('form')
      const formSubmit = form?.querySelector('input[type="submit"], button[type="submit"], button:not([type])')
      if (formSubmit) {
        formSubmit.removeAttribute('disabled')
      }
    }
  }

  collapseSubsequentSteps(triggerElement) {
    const currentStep = triggerElement.closest('[data-controller="stepper-step"]')
    if (!currentStep) return

    let sibling = currentStep.nextElementSibling
    while (sibling) {
      const content = sibling.querySelector('.content')
      if (content) {
        content.classList.remove('h-full', 'p-8', 'border-t-2')
        content.classList.add('h-0', 'overflow-hidden')

        // Clear parent target inputs in subsequent steps so stale values
        // aren't sent in the cascade reload request
        content.querySelectorAll('[data-settings-select-next-target="parent"]').forEach(input => {
          if (input.type === 'radio' || input.type === 'checkbox') {
            input.checked = false
          } else if (input.type === 'hidden') {
            input.value = ''
          }
        })
      }
      sibling = sibling.nextElementSibling
    }
  }

  reload(e) {
    // Collapse steps below the one that triggered the reload
    this.collapseSubsequentSteps(e.target)

    // Clear any previous error state
    this.clearError()

    this.spinnerTargets.forEach((spinner) => {
      if (!spinner.hasAttribute("data-has-data") || spinner.dataset.hasData == 'false') {
        spinner.classList.remove("hidden")
      }
    })

    if (this.hasTextTarget) {
      this.textTargets.forEach((text) => {
        if (!text.hasAttribute("data-has-data") || text.dataset.hasData == 'false') {
          text.classList.add("hidden")
        }
      })
    }

    // Add the ID of the field you want to be tracked when its value changes
    const field_ids = [
      "account_type",
      "login_customer_id",
      "linked_customer_id",
      "business_id",
      "organization_id",
      "linked_in_organization_id",
      "ad_account_id",
      "snapchat_app_id",
      "microsoft_customer_id",
      "microsoft_account_id",
      "property_id",
      "integration_type",
      "application_types",
      "ga4_datastream_ids_web",
      "ga4_datastream_ids_ios",
      "ga4_datastream_ids_android",
      "ga4_ios_api_secret",
      "ga4_android_api_secret",
      "ga4_web_api_secret",
      "cm360_account_id",
      "cm360_user_account_id",
      "customer_match_terms_accepted",
      "dv360_partner_id",
      "dv360_advertiser_id",
      "meta_pixel_id",
      "amazon_manager_account_id",
      "amazon_dsp_advertiser_id"
    ]
    let query = {}

    field_ids.forEach((field_id) => {
      let field = this.parentTargets.filter((input) => {
        if (!input.id.includes(field_id)) return false
        // Hidden fields are always "selected" if they have a value
        if (input.type === "hidden") return input.value.length > 0
        // Radio/checkbox inputs must be checked
        return input.checked
      })
      if (field.length == 1) { // account for single value fields
        query[field_id] = field.pop().value
      } else if (field.length > 1) { // account for multi value fields
        query[field_id] = []
        field.forEach((f) => {
          query[field_id].push(f.value)
        })
      }
    })

    // Check if the triggering element has a custom reload URL override
    // This allows elements like DV360 radio buttons to specify their own endpoint
    // even when rendered outside their intended controller scope (e.g., via ViewComponent)
    const reloadUrl = e.target.dataset.reloadUrl || this.urlValue

    // If using a custom reload URL, prioritize the changed element's value
    // This prevents conflicts when multiple platforms use the same field name (e.g., login_customer_id)
    if (e.target.dataset.reloadUrl && e.target.name && e.target.value) {
      // Extract the field name from the input's name attribute
      // e.g., "integration_data_manager_advertiser_connection[...][login_customer_id]" -> "login_customer_id"
      const nameMatch = e.target.name.match(/\[(\w+)\]$/)
      if (nameMatch) {
        const fieldName = nameMatch[1]
        // Override with the specific value from the changed element
        query[fieldName] = e.target.value
      }
    }

    // Store for refresh
    this.lastReloadUrl = reloadUrl
    this.lastQuery = { ...query }

    this._fetchAccounts(reloadUrl, query)
  }

  refresh() {
    const reloadUrl = this.lastReloadUrl || this.urlValue
    // Signal a hard refresh so the controller busts its cached lookups
    // via `cached_fetch` (see Account::BaseController#cached_fetch).
    const query = { ...(this.lastQuery || {}), refresh: true }

    if (!reloadUrl) return

    this.clearError()

    // Show spinners
    this.spinnerTargets.forEach((spinner) => {
      spinner.classList.remove("hidden")
    })

    // Hide text content while loading
    if (this.hasTextTarget) {
      this.textTargets.forEach((text) => {
        text.classList.add("hidden")
      })
    }

    this._fetchAccounts(reloadUrl, query)
  }

  clearError() {
    this.errorTargets.forEach((el) => el.classList.add("hidden"))
  }

  _fetchAccounts(url, query) {
    get(url, {
      query: query,
      responseKind: "turbo-stream"
    }).then((response) => {
      this.spinnerTargets.forEach((spinner) => {
        if (!spinner.hasAttribute("data-has-data")) {
          spinner.classList.add("hidden")
        }
      })

      if (!response.ok) {
        this.errorTargets.forEach((el) => el.classList.remove("hidden"))
      }
    }).catch(() => {
      // Network error or request failed entirely
      this.spinnerTargets.forEach((spinner) => {
        spinner.classList.add("hidden")
      })
      this.errorTargets.forEach((el) => el.classList.remove("hidden"))
    })
  }
}
